import { useEffect, useState } from "react";
import Accordion from "./accordian";
import TaskCard from "./taskCard";
import { getTaskTemplate, saveTaskTemplate } from "../../utils/firebasefunctions";
import { getAlldealforAdmin, getUsers, backfillActiveDeals, backfillBuyerFileRequestsToPipeline, archiveConvertedRecords } from "../../utils/firebasefunctions";
import { ACTIVE_STAGES } from "../../utils/taskSections";
import { cleanTemplate } from "../../utils/taskSections";
import SectionRow from "./sectionRow";
import { groupTemplateItems, isSectionItem } from "../../utils/taskSections";
import { STARTER_CHECKLIST } from "../../utils/starterChecklist";

function MainTaskTemplate() {
  const [tasks, setTasks] = useState({});
  const [editingTaskId, setEditingTaskId] = useState(null);
  const STATUSES = [
    "Prospect",
    "On Hold",
    "CMAs",
    "Listing Appointment",
    "Pre-Market",
    "Coming Soon",
    "Active",
    "Under Contract",
    "Pending",
    "Closed",
    "Cancelled",
    "Expired",
  ];

  const addNewTask = async (status) => {
    const newTask = {
      id: Date.now(),
      type: "task",
      title: "",
      assignee: null,
      deadline: "",
      image: null,
      createdAt: new Date(),
    };

    const updatedTasks = {
      ...tasks,
      [status]: [newTask, ...(tasks[status] || [])],
    };

    setTasks(updatedTasks);
    setEditingTaskId(newTask.id);

    await saveTaskTemplate(cleanTemplate(updatedTasks));
  };

  const addNewSection = async (status) => {
    const newSection = { type: "section", id: Date.now(), title: "New Section" };
    const updatedTasks = { ...tasks, [status]: [...(tasks[status] || []), newSection] };
    setTasks(updatedTasks);
    await saveTaskTemplate(cleanTemplate(updatedTasks));
  };

  const importStarterChecklist = async () => {
    const hasContent = Object.values(tasks || {}).some((arr) => Array.isArray(arr) && arr.length > 0);
    const msg = hasContent
      ? "This replaces your current Task Template with the starter checklist. Continue?"
      : "Import the starter checklist into your Task Template?";
    if (!window.confirm(msg)) return;
    setTasks(STARTER_CHECKLIST);
    await saveTaskTemplate(cleanTemplate(STARTER_CHECKLIST));
  };

  const backfillActive = async () => {
    try {
      const [deals, users] = await Promise.all([getAlldealforAdmin("deals"), getUsers()]);
      const active = (deals || []).filter((d) => !d.archived && ACTIVE_STAGES.includes(d.dealStage));
      if (!window.confirm(`Add the current checklist to ${active.length} active deal(s)? Finished and archived deals are skipped. Safe to re-run — it won't create duplicates.`)) return;
      const usersById = (users || []).reduce((m, u) => { m[u.id] = u; return m; }, {});
      const res = await backfillActiveDeals(deals || [], tasks, usersById, new Date().toISOString());
      window.alert(`Backfill complete: updated ${res.updated} deal(s), skipped ${res.skipped}.`);
    } catch (e) {
      console.log("Backfill error", e);
      window.alert("Backfill failed — see console.");
    }
  };

  // One-time migration for the "buyer file-requests live in the Buyers Pipeline"
  // change: flags existing buyer file-request leads as File Requested. The board
  // already hides those cards via a filter; this just lights up the pipeline.
  const migrateBuyerFileRequests = async () => {
    if (
      !window.confirm(
        "Move existing buyer file-requests off the Deals Board into the Buyers Pipeline (flags their leads as File Requested)? Safe to re-run.",
      )
    )
      return;
    try {
      const res = await backfillBuyerFileRequestsToPipeline();
      window.alert(
        `Done: flagged ${res.flagged} buyer lead(s), skipped ${res.skipped}.`,
      );
    } catch (e) {
      console.log("Buyer file-request migration error", e);
      window.alert("Migration failed — see console.");
    }
  };

  // Cleanup for leads/appointments that converted but were never archived —
  // their property is already on the Deals Board, yet they linger in the
  // pipelines and at the bottom of the Status Sheet. Shows the exact records
  // first (dry run), applies only after confirmation. Status flips only —
  // nothing is deleted. Safe to re-run.
  const archiveConverted = async () => {
    try {
      const plan = await archiveConvertedRecords({ dryRun: true });
      const total =
        plan.listingLeadsToArchive.length +
        plan.buyerLeadsToArchive.length +
        plan.appointmentsToWin.length;
      if (total === 0) {
        window.alert("Nothing to clean up — no converted leads are lingering.");
        return;
      }
      const preview = [
        ...plan.listingLeadsToArchive.map((x) => `• Listing lead: ${x.label}`),
        ...plan.buyerLeadsToArchive.map((x) => `• Buyer lead: ${x.label}`),
        ...plan.appointmentsToWin.map((x) => `• Appointment → Won: ${x.label}`),
      ].join("\n");
      if (
        !window.confirm(
          `Archive ${total} record(s) whose property is already on the board?\n\n${preview}\n\nStatus flips only — nothing is deleted. Safe to re-run.`,
        )
      )
        return;
      const res = await archiveConvertedRecords();
      window.alert(
        `Done: archived ${res.listingLeadsToArchive.length} listing lead(s), ` +
          `${res.buyerLeadsToArchive.length} buyer lead(s), marked ` +
          `${res.appointmentsToWin.length} appointment(s) won` +
          (res.failed ? ` — ${res.failed} write(s) FAILED, re-run to retry.` : "."),
      );
    } catch (e) {
      console.log("Archive converted leads error", e);
      window.alert("Cleanup failed — see console.");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const data = await getTaskTemplate();
      setTasks(data || {});
      console.log('data', data)
    };

    fetchData();
  }, []);

  return (
    <div>
      <div className="flex justify-end gap-2 px-2 mb-3">
        <button
          onClick={backfillActive}
          className="text-sm font-semibold text-[#1E293B] bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg transition"
        >
          Backfill Active Deals
        </button>
        <button
          onClick={migrateBuyerFileRequests}
          className="text-sm font-semibold text-[#1E293B] bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg transition"
        >
          Migrate Buyer File-Requests
        </button>
        <button
          onClick={archiveConverted}
          className="text-sm font-semibold text-[#1E293B] bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg transition"
        >
          Archive Converted Leads
        </button>
        <button
          onClick={importStarterChecklist}
          className="text-sm font-semibold text-white bg-[#1E293B] hover:bg-green-600 px-4 py-2 rounded-lg transition"
        >
          Import Starter Checklist
        </button>
      </div>
      {/* <header className="flex flex-col lg:flex-row justify-between items-center text-center lg:text-left px-10 py-7 lg:py-5 border-b border-black/10 bg-white">
        <div className="page-title">
          <h2 className="text-3xl sm:text-4xl font-bold bg-[#1E293B] bg-clip-text text-transparent">
            Task Template
          </h2>
        </div>
      </header> */}
      <div className="rounded-lg">
        {STATUSES.map((status) => {
          const statusTasks = tasks[status] || [];
          const taskCount = statusTasks.filter((it) => !isSectionItem(it)).length;
          const groups = groupTemplateItems(statusTasks);

          return (
            <Accordion
              key={status}
              title={status}
              count={taskCount}
              onAdd={() => addNewTask(status)}
              onAddSection={() => addNewSection(status)}
            >
              {/* HEADER */}
              <div className="justify-between w-full text-sm items-center flex">
                <div className="w-[45%] lg:w-[45%] ml-2">Task Name</div>
                <div className="w-[15%] lg:w-[20%] text-center">Assign</div>
                <div className="w-[25%] lg:w-[20%] text-center">Attachments</div>
                <div className="w-[10%] lg:w-[15%] text-center mr-3 md:mr-0">Action</div>
              </div>

              {/* GROUPS */}
              {taskCount === 0 && groups.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-3">No tasks</p>
              ) : (
                groups.map((g) => (
                  <div key={g.section ? g.section.id : "loose"}>
                    {g.section && (
                      <SectionRow
                        section={g.section}
                        status={status}
                        tasks={tasks}
                        setTasks={setTasks}
                        setEditingTaskId={setEditingTaskId}
                      />
                    )}
                    {g.tasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        tasks={tasks}
                        setTasks={setTasks}
                        status={status}
                        editingTaskId={editingTaskId}
                        setEditingTaskId={setEditingTaskId}
                      />
                    ))}
                  </div>
                ))
              )}
            </Accordion>
          );
        })}
      </div>
    </div>
  );
}

export default MainTaskTemplate;
